/**
 * agentComposition — composite-agent grouping for the Agents list.
 *
 * A "wrapper" agent's graph dispatches OTHER deployed agents as sub-workflows;
 * the backend list endpoint surfaces those child marketplace slugs on each row
 * as `composedOf: [slug, ...]` (stamped at deploy from the graph's static
 * `dispatchesWorkflow` node markers; see backend workflow-composition.js).
 *
 * groupAgents() applies the LIST-presentation rules:
 *   - A row whose slug (workflowType OR marketplaceSlug) appears in ANY OTHER
 *     row's member set is WRAPPED → hidden from the top level. This applies
 *     recursively for free: a hidden wrapper's own composedOf still hides ITS
 *     members (membership is computed over ALL rows, not just visible ones).
 *   - The wrapper row gets `members: [row, ...]` — the DEPLOYED member rows
 *     resolved in dispatch order, so the UI can render their icons as a strip.
 *   - Dangling reference (member slug with no deployed row) → resolved into
 *     `membersMissing: [slug, ...]` so the UI can SAY a declared child is not
 *     deployed (that dispatch 404s at run time — "Child workflow not found"),
 *     instead of silently under-counting. Never crashes. Two wrappers sharing
 *     a member → member hidden once, resolved into BOTH strips.
 *     Self-reference → ignored (a row never hides itself).
 *
 * Pure + non-mutating: input rows are never modified; wrapper rows are shallow
 * -copied to attach `members`. Detail pages / deep links are unaffected — this
 * only shapes the LIST.
 */

export const rowMatchesSlug = (row, slug) =>
  !!row && (row.workflowType === slug || row.marketplaceSlug === slug);

/**
 * isMemberOf — is `row` the member that `parent` dispatches under `slug`?
 *
 * The deploy cascade stamps `childBindings: {slug: uuid}` on the parent and
 * the dispatch door reads ONLY that (backend services/child-bindings.js), so
 * the list folds by the BOUND uuid too. Matching by slug alone hid every
 * standalone deploy of the same template ("frontend-specialist2", same
 * marketplaceSlug) inside the wrapper's "+N" chip, unreachable from the list.
 * No slug fallback: a declared slug with no binding is exactly what the
 * dispatch door 404s on, so the list reports it MISSING rather than guessing
 * a same-named row (a fallback that can disagree is the two-places bug).
 */
export const isMemberOf = (parent, slug, row) => {
  if (!row || row === parent || !row.uuid) return false;
  const bindings = parent?.childBindings && typeof parent.childBindings === 'object'
    ? parent.childBindings : null;
  if (!bindings) return false;
  if (bindings[slug] === row.uuid) return true;
  // `composedOf` carries the DEPLOYED MEMBER ROW NAME (backend deploy stamp:
  // `memberPlan.map((m) => m.workflowName)`), which differs from the binding
  // KEY (the declared slug) when the stack had to scope the member's name —
  // `frontend-specialist-browser` under the key `browser` because a bare
  // `browser` row already belonged to someone else. Identity is STILL the
  // bound uuid: the name only picks which bound row this entry means.
  return row.workflowType === slug && Object.values(bindings).includes(row.uuid);
};

/**
 * parentAgentOf — the deployed agent that DISPATCHES `row`, or null.
 * Membership is the bound uuid (isMemberOf), never a slug match — a
 * same-named standalone deploy of a member template is nobody's child.
 */
export function parentAgentOf(row, rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!row?.uuid) return null;
  return list.find((p) => p && p.uuid !== row.uuid
    && (Array.isArray(p.composedOf) ? p.composedOf : [])
      .some((slug) => typeof slug === 'string' && slug && !rowMatchesSlug(p, slug) && isMemberOf(p, slug, row)))
    || null;
}

/**
 * inheritedAgentVendor — the vendor a dispatched sub-agent INHERITS from the
 * stack that owns it: walk the membership edge upward (parentAgentOf — bound
 * uuid, recursive, cycle-guarded) and return the first ancestor whose list
 * row resolves a vendor. List rows carry the RESOLVED vendor (the backend
 * list projection runs the executor's own resolveRunVendor: node pins first,
 * then the row's aiAgent), so a parent whose model was picked on its node
 * modals answers here too — no extra fetches. Honest runtime mirror: an
 * in-process child dispatch runs inside the parent's container, whose
 * AGENT_TYPE is exactly this value. '' when no ancestor resolves one — the
 * locked disc then stays an honest bare "Fixed".
 */
export function inheritedAgentVendor(row, rows) {
  const seen = new Set();
  let cur = row;
  while (cur?.uuid && !seen.has(cur.uuid)) {
    seen.add(cur.uuid);
    const p = parentAgentOf(cur, rows);
    if (!p) return '';
    const v = typeof p.aiAgent === 'string' ? p.aiAgent.trim() : '';
    if (v) return v;
    cur = p;
  }
  return '';
}

/**
 * deleteCascadeDrop — which cached rows leave when ONE agent is deleted.
 *
 * The server's delete CASCADES into the agent's platform-owned member rows
 * (backend workflows.deleteSubgraphChildren), so an optimistic cache drop must
 * mirror it or the members ORPHAN client-side: their wrapper gone, groupAgents
 * un-hides them and they surface as top-level agents until the next natural
 * refetch (which useDeleteAgent deliberately defers) — the deleted-children
 * flash. Mirrored rules, from the list row's own fields:
 *   - the deleted row's `composedOf` names its members;
 *   - `platformOwned` is THE cascade predicate, shipped by the backend
 *     (workflow-slugs.isPlatformOwnedChild — one decider, never re-derived
 *     here from hidden/systemManaged guesswork);
 *   - a member ANOTHER surviving agent still declares is KEPT, exactly like
 *     the server keeps it.
 *
 * @param {Array} rows     the cached list rows (pre-delete)
 * @param {(row)=>boolean} matches  identifies the deleted agent's own row
 * @returns {(row)=>boolean} true = drop this row from the cache
 */
export function deleteCascadeDrop(rows, matches) {
  const list = Array.isArray(rows) ? rows : [];
  const deleted = list.find((a) => matches(a));
  const memberSlugs = (Array.isArray(deleted?.composedOf) ? deleted.composedOf : [])
    .filter((s) => typeof s === 'string' && s);
  const stillDeclared = new Set();
  list.forEach((a) => {
    if (matches(a)) return;
    (Array.isArray(a?.composedOf) ? a.composedOf : []).forEach((s) => stillDeclared.add(s));
  });
  const cascaded = (a) =>
    a?.platformOwned === true
    && memberSlugs.some((s) => isMemberOf(deleted, s, a) && !stillDeclared.has(s));
  return (a) => matches(a) || cascaded(a);
}

/**
 * descendantMembers — ALL of a wrapper's deployed member rows, RECURSIVELY:
 * a member that is itself a wrapper contributes ITS members too
 * (frontend-specialist brings its browser), every level down. This is the
 * display truth for "how big is this crew" (founder rule 2026-08-24: the +N
 * chip and the icon cloud describe the whole tree, e.g. MAGNUM = 7 direct
 * + 1 nested = 8) — groupAgents keeps returning DIRECT members unchanged, so
 * existing consumers of its API are untouched.
 *
 * BFS keeps dispatch order (direct members first, then theirs); cycle-safe by
 * uuid. A declared slug with no deployed row is MISSING at ANY level and is
 * reported, never silently skipped — that dispatch 404s at run time and the
 * wrapper's row is the only surface that can say so (child rows are hidden).
 *
 * @param {object} row   the wrapper row (a groupAgents output row works too)
 * @param {Array}  rows  the FULL row list (visible + hidden members)
 * @returns {{ members: Array, missing: string[] }}
 */
export function descendantMembers(row, rows) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];
  const slugsOf = (r) => (Array.isArray(r?.composedOf) ? r.composedOf : [])
    .filter((s) => typeof s === 'string' && s && !rowMatchesSlug(r, s));
  const members = [];
  const missing = [];
  // The root is pre-seen: a cycle that leads back to the wrapper must never
  // list the wrapper as its own descendant.
  const seen = new Set(row?.uuid ? [row.uuid] : []);
  const queue = [row];
  while (queue.length) {
    const parent = queue.shift();
    for (const slug of slugsOf(parent)) {
      const found = list.find((r) => isMemberOf(parent, slug, r));
      if (!found) { missing.push(slug); continue; }
      if (!found.uuid || seen.has(found.uuid)) continue;
      seen.add(found.uuid);
      members.push(found);
      queue.push(found);
    }
  }
  return { members, missing };
}

export function groupAgents(rows) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];

  // Effective member slugs per row = composedOf minus self-references.
  const memberSlugsOf = (row) => {
    const slugs = Array.isArray(row.composedOf) ? row.composedOf : [];
    return slugs.filter((s) => typeof s === 'string' && s && !rowMatchesSlug(row, s));
  };

  // Wrapped = referenced by ANY OTHER row (visible or itself hidden — that's
  // what makes nesting recursive).
  const wrapped = new Set();
  for (const row of list) {
    for (const slug of memberSlugsOf(row)) {
      for (const other of list) {
        if (isMemberOf(row, slug, other)) wrapped.add(other);
      }
    }
  }

  return list
    .filter((row) => !wrapped.has(row))
    .map((row) => {
      const membersMissing = [];
      const members = memberSlugsOf(row)
        .map((slug) => {
          const found = list.find((r) => isMemberOf(row, slug, r));
          if (!found) membersMissing.push(slug);
          return found;
        })
        .filter(Boolean);
      if (!members.length && !membersMissing.length) return row;
      return { ...row, members, ...(membersMissing.length ? { membersMissing } : {}) };
    });
}

export default groupAgents;
